import React, { useState, useEffect } from 'react';
import { api } from '@/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Plus, Scale, Filter } from 'lucide-react';

const STATUS_COLORS = {
  potential_dispute: 'bg-amber-100 text-amber-700',
  internal_escalation: 'bg-orange-100 text-orange-700',
  negotiation_active: 'bg-blue-100 text-blue-700',
  formal_dispute: 'bg-red-100 text-red-700',
  referral_in_preparation: 'bg-purple-100 text-purple-700',
  forum_active: 'bg-indigo-100 text-indigo-700',
  awaiting_decision: 'bg-cyan-100 text-cyan-700',
  decision_issued: 'bg-teal-100 text-teal-700',
  compliance_pending: 'bg-amber-100 text-amber-700',
  settled: 'bg-green-100 text-green-700',
  closed: 'bg-slate-100 text-slate-600',
  archived: 'bg-slate-50 text-slate-400',
};

const RISK_COLORS = { low: 'bg-green-100 text-green-700', medium: 'bg-amber-100 text-amber-700', high: 'bg-red-100 text-red-700', critical: 'bg-red-600 text-white' };

export default function DisputeRegister({ projectId, settings, onOpenMatter, onCreateNew }) {
  const [matters, setMatters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRisk, setFilterRisk] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const params = {};
        if (filterStatus) params.status = filterStatus;
        if (filterRisk) params.risk = filterRisk;
        const res = await api.disputes.getRegister(projectId, params);
        setMatters(res.data.items || []);
        setTotal(res.data.total || 0);
      } catch { /* silent */ } finally { setLoading(false); }
    })();
  }, [projectId, filterStatus, filterRisk]);

  const filtered = matters.filter(m => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (m.title || '').toLowerCase().includes(s) || (m.matter_ref || '').toLowerCase().includes(s) || (m.counterparty || '').toLowerCase().includes(s);
  });

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>;

  return (
    <div className="space-y-4" data-testid="dispute-register">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-barlow font-bold uppercase tracking-tight">
          Dispute Register
          <span className="ml-2 text-sm font-normal text-slate-400">({total} matters)</span>
        </h2>
        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1" onClick={onCreateNew} data-testid="create-dispute-from-register">
          <Plus className="h-4 w-4" /> New Dispute
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input placeholder="Search matters..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8" data-testid="register-search" />
        </div>
        <Select value={filterStatus} onValueChange={v => setFilterStatus(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="potential_dispute">Potential Dispute</SelectItem>
            <SelectItem value="formal_dispute">Formal Dispute</SelectItem>
            <SelectItem value="negotiation_active">Negotiation Active</SelectItem>
            <SelectItem value="forum_active">Forum Active</SelectItem>
            <SelectItem value="awaiting_decision">Awaiting Decision</SelectItem>
            <SelectItem value="settled">Settled</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterRisk} onValueChange={v => setFilterRisk(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="All Risks" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Risks</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Register Table */}
      {filtered.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          <Scale className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-lg font-medium text-slate-500">No disputes found</p>
          <p className="text-sm text-slate-400 mt-1">Create a new dispute to start tracking a matter</p>
          <Button className="mt-4 bg-slate-900 hover:bg-slate-800" onClick={onCreateNew}>Create First Dispute</Button>
        </CardContent></Card>
      ) : (
        <div className="border rounded-lg overflow-hidden bg-white">
          <table className="w-full text-sm" data-testid="register-table">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left px-3 py-2.5 text-[10px] font-bold uppercase text-slate-500">Ref</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-bold uppercase text-slate-500">Matter Title</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-bold uppercase text-slate-500">Counterparty</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-bold uppercase text-slate-500">Status</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-bold uppercase text-slate-500">Risk</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-bold uppercase text-slate-500">Amount</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-bold uppercase text-slate-500">Issues</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-bold uppercase text-slate-500">Next Deadline</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(m => (
                <tr key={m.id} className="border-b hover:bg-slate-50 cursor-pointer" onClick={() => onOpenMatter(m.id)} data-testid={`register-row-${m.id}`}>
                  <td className="px-3 py-2.5 font-mono text-xs text-slate-400">{m.matter_ref}</td>
                  <td className="px-3 py-2.5 font-medium text-slate-800 max-w-[220px]"><p className="truncate">{m.title}</p></td>
                  <td className="px-3 py-2.5 text-xs text-slate-600">{m.counterparty || '-'}</td>
                  <td className="px-3 py-2.5"><Badge className={`text-[10px] ${STATUS_COLORS[m.status] || 'bg-slate-100'}`}>{m.status?.replace(/_/g, ' ')}</Badge></td>
                  <td className="px-3 py-2.5"><Badge className={`text-[10px] ${RISK_COLORS[m.risk_rating] || 'bg-slate-100'}`}>{m.risk_rating}</Badge></td>
                  <td className="px-3 py-2.5 text-xs font-mono">{m.amount_in_dispute ? new Intl.NumberFormat('en-ZA', { style: 'currency', currency: m.currency || 'ZAR', maximumFractionDigits: 0 }).format(m.amount_in_dispute) : '-'}</td>
                  <td className="px-3 py-2.5 text-xs text-center">{m.issue_count || 0}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-500">{m.next_deadline_date || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
